import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PageLoader } from "@/components/ui/PageLoader";
import { prefetchCommonRoutes } from "@/utils/routePrefetch";
import { PWAUpdatePrompt } from "@/components/pwa/PWAUpdatePrompt";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OfflineIndicator } from "@/components/OfflineIndicator";

// Lazy load toast for deferred loading
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const GenniPage = lazy(() => import("./pages/GenniPage"));
const NewReadingPage = lazy(() => import("./pages/NewReadingPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ReadingDetailPage = lazy(() => import("./pages/ReadingDetailPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const ChangeEmailPage = lazy(() => import("./pages/ChangeEmailPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const InstallPage = lazy(() => import("./pages/InstallPage"));

// Optimized React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Background prefetch common routes after initial render
const usePrefetchRoutes = () => {
  useEffect(() => {
    prefetchCommonRoutes();
  }, []);
};

const App = () => {
  usePrefetchRoutes();
  
  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Sonner />
          </Suspense>
          <PWAUpdatePrompt />
          <OfflineIndicator />
          <BrowserRouter>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <GenniPage />
                </ProtectedRoute>
              } />
              <Route path="/new-reading" element={
                <ProtectedRoute>
                  <NewReadingPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/reading/:reportId" element={
                <ProtectedRoute>
                  <ReadingDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/settings/password" element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              } />
              <Route path="/settings/email" element={
                <ProtectedRoute>
                  <ChangeEmailPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/install" element={<InstallPage />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
